import { Link } from "react-router-dom";
import type { DashboardStats } from "@codestack/shared";
import { Badge, StatusBadge } from "@codestack/ui";
import { useApi } from "../lib/useApi";
import { useAuth } from "../lib/auth";
import { ErrorState, LoadingState } from "../components/States";
import { dateTime, money, percent } from "../lib/format";

export function DashboardPage() {
  const { user, can } = useAuth();
  const { data, error, loading, reload } = useApi<DashboardStats>("/admin/reports/dashboard");

  if (loading && !data) return <LoadingState label="Loading dashboard…" />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return null;

  const cards = [
    {
      label: "Volume today",
      value: money(data.volumeTodayMinor, data.currency),
      hint: `${data.transactionsToday} transactions`,
    },
    {
      label: "Volume this month",
      value: money(data.volumeMonthMinor, data.currency),
      hint: `${data.transactionsMonth} transactions`,
    },
    {
      label: "Success rate",
      value: percent(data.successRate),
      hint: `${data.failedToday} failed today`,
    },
    {
      label: "Active platforms",
      value: String(data.activePlatforms),
      hint: `${data.totalPlatforms} total`,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h1 className="font-display text-xl font-bold text-white">
          Welcome back, {user?.name.split(" ")[0]}
        </h1>
        <p className="text-sm text-ink-400">
          Live figures from the payments ledger. All amounts in {data.currency}.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <div key={card.label} className="admin-card p-5">
            <p className="admin-label">{card.label}</p>
            <p className="admin-value mt-2.5">{card.value}</p>
            <p className="mt-1 text-xs text-ink-500">{card.hint}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="admin-card p-5 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white">Recent payments</h2>
            {can("transaction:read") ? (
              <Link
                to="/transactions"
                className="text-xs font-medium text-brand-300 hover:text-brand-200"
              >
                View all
              </Link>
            ) : null}
          </div>

          {data.recentPayments.length === 0 ? (
            <p className="py-10 text-center text-sm text-ink-500">
              No payments recorded yet.
            </p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-[11px] uppercase tracking-[0.1em] text-ink-500">
                  <tr>
                    <th className="pb-2 font-semibold">Reference</th>
                    <th className="pb-2 font-semibold">Platform</th>
                    <th className="pb-2 text-right font-semibold">Amount</th>
                    <th className="pb-2 text-right font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.06]">
                  {data.recentPayments.map((payment) => (
                    <tr key={payment.id}>
                      <td className="py-3">
                        <Link
                          to={`/transactions/${payment.id}`}
                          className="font-mono text-[11px] text-ink-200 hover:text-brand-300"
                        >
                          {payment.reference}
                        </Link>
                        <p className="mt-0.5 text-[10px] text-ink-600">
                          {dateTime(payment.createdAt)}
                        </p>
                      </td>
                      <td className="py-3 text-xs text-ink-400">
                        {payment.platformName}
                      </td>
                      <td className="py-3 text-right font-mono text-xs text-ink-200">
                        {money(payment.amountMinor, payment.currency)}
                      </td>
                      <td className="py-3 text-right">
                        <StatusBadge status={payment.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="admin-card p-5">
            <h2 className="text-sm font-semibold text-white">Queue</h2>
            <dl className="mt-4 space-y-3">
              {[
                ["Queued", data.queue.queued],
                ["Running", data.queue.running],
                ["Failed", data.queue.failed],
                ["Dead", data.queue.dead],
              ].map(([label, value]) => (
                <div key={String(label)} className="flex items-center justify-between">
                  <dt className="text-xs text-ink-400">{label}</dt>
                  <dd className="font-mono text-xs text-ink-200">{value}</dd>
                </div>
              ))}
            </dl>
            <Link
              to="/health"
              className="mt-4 block text-xs font-medium text-brand-300 hover:text-brand-200"
            >
              System health →
            </Link>
          </div>

          <div className="admin-card p-5">
            <h2 className="text-sm font-semibold text-white">Webhooks (24h)</h2>
            <dl className="mt-4 space-y-3">
              {[
                ["Delivered", data.webhooks.delivered],
                ["Pending", data.webhooks.pending],
                ["Failed", data.webhooks.failed],
              ].map(([label, value]) => (
                <div key={String(label)} className="flex items-center justify-between">
                  <dt className="text-xs text-ink-400">{label}</dt>
                  <dd className="font-mono text-xs text-ink-200">{value}</dd>
                </div>
              ))}
            </dl>
            {data.webhooks.failed > 0 ? (
              <div className="mt-4">
                <Badge tone="warning">Attention required</Badge>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
